import {
  buildModuleCatalogFromOlFeatures,
  isPointInPolygonRing
} from '@/utils/debug/defectModuleGeoVerify';

/**
 * 지도 클릭 좌표(EPSG:4326 [lon, lat])가 siteDesign 모듈 폴리곤 안인지 판별.
 * solarModuleLayer 가 보이지 않을 때 forEachFeatureAtPixel 대신 사용한다.
 *
 * @param {number} lon
 * @param {number} lat
 * @param {import('ol/Feature').default[]} olFeatures
 * @returns {object|null} highlight / drawer 동기화용 모듈 메타
 */
export function findSolarModuleAtCoordinate(lon, lat, olFeatures) {
  const lng = Number(lon);
  const latitude = Number(lat);
  if (!Number.isFinite(lng) || !Number.isFinite(latitude)) {
    return null;
  }

  const features = Array.isArray(olFeatures) ? olFeatures : [];
  if (!features.length) {
    return null;
  }

  const catalog = buildModuleCatalogFromOlFeatures(features);
  if (!catalog.length) {
    return null;
  }

  const containing = catalog.filter(mod => isPointInPolygonRing(lng, latitude, mod.ring));
  if (!containing.length) {
    return null;
  }

  const picked = containing.length === 1
    ? containing[0]
    : containing.reduce((best, mod) => {
        const bestArea = polygonRingArea(best.ring);
        const modArea = polygonRingArea(mod.ring);
        return modArea < bestArea ? mod : best;
      });

  const feature = features.find(f => {
    const props = f?.getProperties?.() ?? {};
    const seq = props.solar_module_seq ?? props.solarModuleSeq ?? props.module_id;
    return seq != null && String(seq) === String(picked.solarModuleSeq);
  });
  const props = feature?.getProperties?.() ?? {};

  return {
    solarModuleSeq: picked.solarModuleSeq,
    module_id: props.module_id ?? props.moduleId ?? picked.moduleId ?? null,
    string_id: props.string_id ?? props.stringId ?? picked.stringId ?? null,
    array_id: props.array_id ?? props.arrayId ?? picked.arrayId ?? null,
    solar_string_seq: props.solar_string_seq ?? props.solarStringSeq ?? null,
    solar_array_seq: props.solar_array_seq ?? props.solarArraySeq ?? null,
    resolution: 'coordinateHit',
    overlappingModuleCount: containing.length
  };
}

/** 작은 폴리곤 우선(겹침 시) — shoelace, WGS84 근사 */
function polygonRingArea(ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return Infinity;
  }
  let area = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = Number(ring[i][0]);
    const yi = Number(ring[i][1]);
    const xj = Number(ring[j][0]);
    const yj = Number(ring[j][1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) {
      continue;
    }
    area += (xj + xi) * (yj - yi);
  }
  return Math.abs(area / 2);
}
